import React, { useState, useEffect } from 'react';
import {
  Server, Shield, Globe, Cpu, Network, RefreshCw, CheckCircle,
  AlertTriangle, Clock, ChevronDown, ChevronUp, Activity, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getConnectors, testConnector, syncConnector } from '../services/api';
import { toast } from 'sonner';

const typeIcons = {
  idp: Shield,
  cloud: Globe,
  observability: Activity,
  agent_framework: Cpu,
};

const typeLabels = {
  idp: 'Identity Provider',
  cloud: 'Cloud Platform',
  observability: 'Observability',
  agent_framework: 'Agent Framework',
};

const healthColors = {
  healthy: '#34C759',
  degraded: '#FFCC00',
  unhealthy: '#FF3B30',
  unknown: '#6B7280',
};

const ConnectorsPage = () => {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => { fetchConnectors(); }, []);

  const fetchConnectors = async () => {
    try {
      const resp = await getConnectors();
      setConnectors(resp.data);
    } catch (err) {
      console.error('Failed to load connectors', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (id) => {
    setActionLoading(`test-${id}`);
    try {
      const resp = await testConnector(id);
      toast.success(`Connection healthy (${resp.data.latency_ms}ms)`);
      fetchConnectors();
    } catch {
      toast.error('Health check failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSync = async (id) => {
    setActionLoading(`sync-${id}`);
    try {
      await syncConnector(id);
      toast.success('Sync completed');
      fetchConnectors();
    } catch {
      toast.error('Sync failed');
    } finally {
      setActionLoading(null);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return `${Math.round(diffH / 24)}d ago`;
  };

  const totalIdentities = connectors.reduce((sum, c) => sum + (c.identities_discovered || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-[#9CA3AF]">Loading connectors...</div></div>;
  }

  return (
    <div data-testid="connectors-page" className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <Network className="w-6 h-6 text-[#0066FF]" />
          <h1 className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>Connectors</h1>
        </div>
        <p className="text-sm text-[#6B7280]">Identity source integrations &middot; Read-only access &middot; {totalIdentities} identities across {connectors.length} connectors</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#0066FF]">{connectors.length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Connected</div>
        </div>
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#F9FAFB]">{totalIdentities}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Identities</div>
        </div>
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#34C759]">{connectors.filter(c => c.health_status === 'healthy').length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Healthy</div>
        </div>
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#FFCC00]">{connectors.filter(c => c.health_status !== 'healthy').length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Attention</div>
        </div>
      </div>

      {/* Connector List */}
      <div className="space-y-3">
        {connectors.map((conn) => {
          const Icon = typeIcons[conn.type] || Server;
          const health = conn.health_status || 'unknown';
          const hColor = healthColors[health];
          const isExpanded = expanded === conn.id;

          return (
            <div key={conn.id} className="enterprise-card" data-testid={`connector-${conn.id}`}>
              <div className="p-5 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : conn.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#0066FF]/10">
                      <Icon className="w-5 h-5 text-[#0066FF]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#F9FAFB] font-semibold">{conn.name}</span>
                        <span className="text-[10px] font-mono text-[#6B7280] uppercase px-2 py-0.5 bg-[#1A1A1A]">{typeLabels[conn.type] || conn.type}</span>
                      </div>
                      <div className="text-[10px] text-[#6B7280] mt-0.5">
                        Last sync: {formatTime(conn.last_sync)} &middot; Version: {conn.version || '—'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="text-center">
                      <div className="text-lg font-mono font-bold text-[#F9FAFB]">{conn.identities_discovered}</div>
                      <div className="text-[10px] text-[#6B7280] uppercase">Identities</div>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5" style={{ backgroundColor: `${hColor}15` }}>
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: hColor }} />
                      <span className="text-xs font-mono capitalize" style={{ color: hColor }}>{health}</span>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-[#6B7280]" /> : <ChevronDown className="w-4 h-4 text-[#6B7280]" />}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="px-5 pb-5 border-t border-[#27272A]/40">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-4">
                    {/* Configuration */}
                    <div>
                      <h3 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Configuration</h3>
                      <div className="space-y-2">
                        {conn.config && Object.entries(conn.config).map(([k, v]) => (
                          <div key={k} className="flex justify-between p-2 bg-[#1A1A1A]">
                            <span className="text-xs text-[#6B7280]">{k.replace(/_/g, ' ')}</span>
                            <span className="text-xs font-mono text-[#9CA3AF]">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Sync History */}
                    <div>
                      <h3 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Recent Syncs</h3>
                      {(conn.sync_history || []).length > 0 ? (
                        <div className="space-y-2">
                          {conn.sync_history.slice(-3).reverse().map((s, i) => (
                            <div key={i} className="p-2 bg-[#1A1A1A] flex items-center justify-between">
                              <div>
                                <div className="text-xs text-[#F9FAFB]">{s.identities_found} identities found</div>
                                <div className="text-[10px] text-[#6B7280]">{new Date(s.started_at).toLocaleString()}</div>
                              </div>
                              <span className={`text-[10px] font-mono px-2 py-0.5 ${s.status === 'completed' ? 'text-[#34C759] bg-[#34C759]/10' : 'text-[#FFCC00] bg-[#FFCC00]/10'}`}>
                                {s.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-3 bg-[#1A1A1A] text-xs text-[#6B7280] text-center">No sync history yet</div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 mt-4 pt-4 border-t border-[#27272A]/40">
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleTest(conn.id); }}
                      disabled={actionLoading === `test-${conn.id}`}
                      className="secondary-btn text-sm"
                      data-testid={`test-${conn.id}`}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      {actionLoading === `test-${conn.id}` ? 'Testing...' : 'Test Connection'}
                    </Button>
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleSync(conn.id); }}
                      disabled={actionLoading === `sync-${conn.id}`}
                      className="secondary-btn text-sm"
                      data-testid={`sync-${conn.id}`}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading === `sync-${conn.id}` ? 'animate-spin' : ''}`} />
                      {actionLoading === `sync-${conn.id}` ? 'Syncing...' : 'Sync Now'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConnectorsPage;
